// ─────────────────────────────────────────────────────────────
// ConfirmDeleteDialog — reusable destructive-action modal
// Uses OpenDAW's built-in Dialog component for consistent theming.
//
// Usage:
//   const confirmed = await confirmDelete({
//       name: "Hype",
//       kind: "type",   // "type" | "category"
//   })
//   if (confirmed) { /* proceed */ }
// ─────────────────────────────────────────────────────────────

import {Dialog} from "@/ui/components/Dialog.tsx"
import {IconSymbol} from "@opendaw/studio-enums"

export interface ConfirmDeleteOptions {
    name: string
    kind: "type" | "category"
    detail?: string
}

export function confirmDelete(opts: ConfirmDeleteOptions): Promise<boolean> {
    const {name, kind, detail} = opts
    const {resolve, promise} = Promise.withResolvers<boolean>()
    let resolved = false

    const done = (result: boolean) => {
        if (!resolved) { resolved = true; resolve(result) }
    }

    // ── Name input ────────────────────────────────────────────
    const nameInput = document.createElement("input")
    nameInput.type      = "text"
    nameInput.className = "st-input"
    Object.assign(nameInput.style, {width: "100%", boxSizing: "border-box", marginTop: "0.25rem"})

    // ── Inline error hint ─────────────────────────────────────
    const hint = document.createElement("div")
    Object.assign(hint.style, {
        fontSize: "0.65rem", color: "var(--color-red, #e05c5c)",
        minHeight: "0.75rem", display: "none", marginTop: "0.2rem",
    })
    hint.textContent = "Name does not match."

    // ── Body text ─────────────────────────────────────────────
    const defaultDetail = kind === "type"
        ? "This will permanently delete this type and remove it from every song that uses it."
        : "This will permanently delete this category and remove it from every song that uses it."
    const bodyText = document.createElement("p")
    Object.assign(bodyText.style, {fontSize: "0.78rem", lineHeight: "1.55", margin: "0"})
    bodyText.textContent = detail ?? defaultDetail

    // ── Label: Type "name" to confirm ─────────────────────────
    const labelEl = document.createElement("p")
    Object.assign(labelEl.style, {fontSize: "0.72rem", margin: "0"})
    labelEl.append("Type ")
    const strong = document.createElement("strong")
    strong.textContent = `"${name}"`
    labelEl.appendChild(strong)
    labelEl.append(" to confirm")

    // ── Content wrapper ───────────────────────────────────────
    const content = document.createElement("div")
    Object.assign(content.style, {
        padding: "0.5em 0", minWidth: "280px",
        display: "flex", flexDirection: "column", gap: "0.6rem",
    })
    content.appendChild(bodyText)
    content.appendChild(labelEl)
    content.appendChild(nameInput)
    content.appendChild(hint)

    // ── Input events ──────────────────────────────────────────
    nameInput.addEventListener("input", () => {
        hint.style.display = (nameInput.value.length > 0 && nameInput.value !== name)
            ? "block" : "none"
    })

    nameInput.addEventListener("keydown", (e: KeyboardEvent) => {
        if (e.key === "Enter" && nameInput.value === name) {
            done(true)
            dialog.close()
        }
    })

    // ── Dialog (uses OpenDAW theme) ───────────────────────────
    const dialog = Dialog(
        {
            headline:  `Delete "${name}"?`,
            icon:      IconSymbol.System,
            cancelable: true,
            buttons: [
                {
                    text: "Cancel",
                    primary: false,
                    onClick: handler => { done(false); handler.close() },
                },
                {
                    text: "Delete",
                    primary: false,
                    onClick: handler => {
                        if (nameInput.value === name) {
                            done(true); handler.close()
                        } else {
                            hint.style.display = "block"
                            nameInput.focus()
                        }
                    },
                },
            ],
        },
        content,
    )

    // Catch Escape / backdrop close
    dialog.addEventListener("close", () => done(false), {once: true})

    document.body.appendChild(dialog)
    dialog.showModal()
    setTimeout(() => nameInput.focus(), 50)

    return promise
}
